import { startCase, toLower } from 'lodash';

import moment from 'moment';

// Document types that are complete the moment they exist — they carry no
// signature and never have a request behind them.
export const noSignatureDocs = [
	'Telehealth',
	'Patient-Intake',
	'Intake-Questionnaire',
];

// Joins the clauses of a tooltip; RANGE_SEPARATOR joins the ends of a date range.
const CLAUSE_SEPARATOR = ' — ';
const RANGE_SEPARATOR = ' – ';

// The real ROI lifecycle, which getPatientDocs otherwise collapses into a
// synthetic COMPLETED request state.
const ROI_STATUSES = {
	active: { label: 'Active', variant: 'success' },
	matched: { label: 'Active', variant: 'success' },
	revoked: { label: 'Revoked', variant: 'danger' },
	expired: { label: 'Expired', variant: 'warning' },
	pending_review: { label: 'Pending review', variant: 'warning' },
	awaiting_signature: { label: 'Awaiting signature', variant: 'neutral' },
	rejected: { label: 'Rejected', variant: 'danger' },
};

const REQUEST_STATE_VARIANTS = {
	APPROVED: 'success',
	COMPLETED: 'success',
	DENIED: 'danger',
	DECLINED: 'danger',
	PENDING_APPROVAL: 'warning',
};

const humanize = (value) => startCase(toLower(value));

// requests.state is a free-form text column and mixes separators
// ('PENDING_APPROVAL' but 'AWAITING SIGNATURE'), so normalise before lookup.
const variantForState = (state) =>
	REQUEST_STATE_VARIANTS[String(state).toUpperCase().replace(/\s+/g, '_')] ||
	'neutral';

// Blank and whitespace-only strings count as absent.
const present = (value) =>
	typeof value === 'string' ? value.trim() || null : value || null;

// Drops absent clauses; null when nothing is left to say.
const joinClauses = (clauses) =>
	clauses.map(present).filter(Boolean).join(CLAUSE_SEPARATOR) || null;

const formatDate = (value) => {
	if (!value) {
		return null;
	}

	const parsed = moment(value);
	return parsed.isValid() ? parsed.format('MMM D, YYYY') : null;
};

const labelledDate = (prefix, value) => {
	const date = formatDate(value);
	return date ? `${prefix} ${date}` : null;
};

const revokedTooltip = ({ revokedAt, revokedReason }) => {
	const date = formatDate(revokedAt);
	const reason = present(revokedReason);

	if (!date && !reason) {
		return null;
	}

	return joinClauses([date ? `Revoked ${date}` : 'Revoked', reason]);
};

// The roi table's clinician_name/clinician_entity are misnomers: they hold the
// RECEIVING PARTY of the disclosure — whatever the patient typed in the ROI
// form's NAME/ENTITY fields, so a relative, an attorney or a school as readily
// as a practitioner. The copy stays neutral for that reason, and either half
// may be blank.
const recipientClause = ({ recipientName, recipientEntity }) => {
	const parties = [recipientName, recipientEntity].map(present).filter(Boolean);

	return parties.length ? `For ${parties.join(', ')}` : null;
};

const validityTooltip = (label) => (roi) =>
	joinClauses([recipientClause(roi), labelledDate(label, roi.expiresAt)]);

// Per-status ROI detail; statuses absent from this map get a bare badge. Like
// the payload tooltips, every clause drops on its own and a tooltip with
// nothing left to say is null.
const roiTooltips = {
	active: validityTooltip('Valid until'),
	matched: validityTooltip('Valid until'),
	expired: validityTooltip('Expired'),
	// A revoked release has no validity left to report, so the expiry is dropped
	// and the revocation detail leads.
	revoked: (roi) => joinClauses([revokedTooltip(roi), recipientClause(roi)]),
};

const enrollmentTooltip = ({ payload, programName }) =>
	joinClauses([programName, labelledDate('Starts', payload.startDate)]);

// Per-type summaries of the linked request's payload. Types absent from this
// map get a badge with no tooltip.
const payloadTooltips = {
	DISCONTINUANCE: ({ payload }) =>
		joinClauses([
			(payload.programSelected || [])
				.map((program) => program && program.label)
				.filter(Boolean)
				.join(', '),
			labelledDate('Effective', payload.effectiveDate),
		]),
	LEAVE: ({ payload }) => {
		const from = formatDate(payload.fromDate);
		const to = formatDate(payload.toDate);

		return joinClauses([
			from && to ? `${from}${RANGE_SEPARATOR}${to}` : from || to,
			payload.reasonForAbsence,
		]);
	},
	// ENROLL payloads carry only programId, so the group name arrives as the
	// programName the query resolves. Enrollment is keyed twice because the two
	// queries speak different vocabularies: getPatientDocs rows carry the
	// userdocument type, getPatientRequests rows the requests.req_type constant
	// (api helpers/constants.js ENROLLMENT_REQUESTS).
	'ENROLLMENT-REQUEST': enrollmentTooltip,
	'Enrollment Request': enrollmentTooltip,
};

const getTooltip = (row) => {
	const buildTooltip = payloadTooltips[row.documentType];

	return buildTooltip
		? buildTooltip({ ...row, payload: row.payload || {} })
		: null;
};

/**
 * Derives the lifecycle status of one patient-documents row.
 *
 * @param {object} row a getPatientDocs or getPatientRequests row
 * @returns {{label: string, variant: 'success'|'danger'|'warning'|'neutral', tooltip: ?string}}
 */
export const getDocumentStatus = (row = {}) => {
	const roiStatus = row.roi && row.roi.status;

	if (roiStatus) {
		const { label, variant } = ROI_STATUSES[roiStatus] || {
			label: humanize(roiStatus),
			variant: 'neutral',
		};
		// Own-property lookup only: a roi.status of 'constructor' would otherwise
		// resolve to Object.prototype's and hand MUI a function as its tooltip
		// title. The status enum is a Waterline-layer guarantee — the column has no
		// CHECK constraint behind it.
		const buildTooltip = Object.prototype.hasOwnProperty.call(
			roiTooltips,
			roiStatus,
		)
			? roiTooltips[roiStatus]
			: null;

		return {
			label,
			variant,
			tooltip: buildTooltip ? buildTooltip(row.roi) : null,
		};
	}

	const tooltip = getTooltip(row);

	if (noSignatureDocs.includes(row.documentType)) {
		return { label: 'Completed', variant: 'success', tooltip };
	}

	const state = row.request && row.request.state;

	if (state) {
		return { label: humanize(state), variant: variantForState(state), tooltip };
	}

	return { label: 'Pending signature', variant: 'neutral', tooltip };
};
